// =============================================================================
// VINUM - Breadcrumb Component
// Navigation breadcrumb with responsive truncation and home icon support
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  /** Show a home icon instead of text (typically for the first item) */
  showHomeIcon?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  /** Custom separator element (defaults to chevron-right icon) */
  separator?: React.ReactNode;
  className?: string;
}

// Default chevron-right separator
const ChevronRight: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('h-4 w-4 flex-shrink-0', className)}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

// Home icon for the first breadcrumb item
const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={cn('h-4 w-4', className)}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z"
    />
  </svg>
);

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  separator,
  className,
}) => {
  if (items.length === 0) return null;

  const separatorNode = separator ?? (
    <ChevronRight className="text-gray-300 dark:text-gray-600" />
  );

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center', className)}>
      <ol className="flex items-center gap-1.5 text-sm">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li key={index} className="flex items-center gap-1.5">
              {/* Separator (skip before first item) */}
              {index > 0 && (
                <span className="flex-shrink-0" aria-hidden="true">
                  {separatorNode}
                </span>
              )}

              {/* Breadcrumb item */}
              {isLast ? (
                // Current page - plain text, not clickable
                <span
                  className={cn(
                    'font-medium text-gray-900 dark:text-gray-100',
                    'truncate max-w-[120px] mobile:max-w-none'
                  )}
                  aria-current="page"
                  title={item.label}
                >
                  {item.showHomeIcon ? <HomeIcon /> : item.label}
                </span>
              ) : item.href ? (
                // Clickable link
                <Link
                  to={item.href}
                  className={cn(
                    'text-gray-500 dark:text-gray-400',
                    'hover:text-gray-700 dark:hover:text-gray-200',
                    'transition-colors duration-150',
                    'truncate max-w-[120px] mobile:max-w-none'
                  )}
                  title={item.label}
                >
                  {item.showHomeIcon ? <HomeIcon /> : item.label}
                </Link>
              ) : (
                // Non-clickable text (no href)
                <span
                  className={cn(
                    'text-gray-500 dark:text-gray-400',
                    'truncate max-w-[120px] mobile:max-w-none'
                  )}
                  title={item.label}
                >
                  {item.showHomeIcon ? <HomeIcon /> : item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

Breadcrumb.displayName = 'Breadcrumb';
